import { useReducer, useState, useCallback, useEffect } from 'react';
import addressFormReducer from 'containers/pages/addressManagement/reducers/addressFormReducer';
import { initAddress, initAddressValid, loadedAddressValid } from 'containers/pages/addressManagement/data';
import { handleAddress, makeAddressObj, convertAddress } from 'containers/pages/addressManagement/libs';
import { getAddress } from 'api/address';
import * as TY from 'containers/pages/addressManagement/constants';
import { useNotificationProvider } from 'hooks';
import { AddressFormData, AddressValidation } from 'containers/pages/addressManagement/types';

interface UseAddressFormUnifiedProps {
  mode: 'add' | 'edit';
  addressId?: number | null;
  customerId?: number | null;
  onSave?: (formData: Record<string, unknown>, isEdit: boolean) => Promise<void>;
}

function useAddressFormUnified({ mode, addressId, customerId, onSave }: UseAddressFormUnifiedProps) {
  const { notifyError } = useNotificationProvider();
  const [formState, dispatch] = useReducer(addressFormReducer, { ...initAddress });
  const [valid, setValid] = useState<AddressValidation>({ ...initAddressValid });
  const [isLoading, setIsLoading] = useState(false);

  // Load Google Maps script for address autocomplete
  useEffect(() => {
    if (window.google) return;

    const script = document.createElement('script');
    script.async = true;
    script.defer = true;
    script.id = 'google-maps';
    script.src = `https://maps.googleapis.com/maps/api/js?key=${import.meta.env.VITE_GOOGLE_API_KEY}&libraries=places&loading=async`;

    script.onerror = () => {
      console.error('Failed to load Google Maps script');
    };

    document.head.appendChild(script);
  }, []);

  // Sync CustomerId from props in add mode
  useEffect(() => {
    if (mode === 'add' && customerId) {
      dispatch({ type: TY.CUSTOMER_ID, payload: customerId });
    }
  }, [mode, customerId]);

  // Load address data when editing
  useEffect(() => {
    if (mode === 'edit' && addressId) {
      const loadAddress = async () => {
        setIsLoading(true);
        try {
          const result = await getAddress(addressId);
          const addressData = result?.Data || result;
          const converted = convertAddress(addressData);
          dispatch({ type: TY.ADDRESS_CONVERT, payload: converted });
          setValid({ ...loadedAddressValid });
        } catch (error) {
          console.error('[useAddressFormUnified] Failed to load address:', error);
          notifyError('Failed to load address data');
        } finally {
          setIsLoading(false);
        }
      };
      loadAddress();
    }
  }, [mode, addressId, notifyError]);

  // Central field change handler
  const onForm = useCallback(
    (e: { target: { name: string; value: string } }) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      handleAddress(e, dispatch, setValid as any);
    },
    [],
  );

  // Handle AddressType autocomplete change
  const onTypeChange = useCallback(
    (_event: unknown, newValue: { Id: number; Title: string } | null) => {
      if (newValue) {
        dispatch({ type: TY.TYPE_ID, payload: newValue.Id });
        dispatch({ type: TY.TYPE_TITLE, payload: newValue.Title });
        setValid((prev) => ({
          ...prev,
          TypeId: { isErr: false, errMsg: '', canNotSave: false },
        }));
      } else {
        dispatch({ type: TY.TYPE_ID, payload: null });
        dispatch({ type: TY.TYPE_TITLE, payload: '' });
        setValid((prev) => ({
          ...prev,
          TypeId: { isErr: true, errMsg: 'Address type is required', canNotSave: true },
        }));
      }
    },
    [],
  );

  // Handle Google Places autocomplete selection (plain function — same pattern as contacts hook)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const onPlace = (placeData: any) => {
    console.log('[onPlace] called with:', placeData);
    const addressComp = placeData.geoCode[0].address_components;

    let street = '';
    let city = '';
    let province = '';
    let postalCode = '';
    let country = '';

    for (let i = 0; i < addressComp.length; i++) {
      const type = addressComp[i].types[0];
      if (type === 'street_number') street = addressComp[i].short_name;
      if (type === 'route') street = street.concat(' ', addressComp[i].short_name);
      if (type === 'neighborhood') street = street.concat(' ', addressComp[i].short_name);
      if (type === 'locality') city = addressComp[i].short_name;
      if (type === 'administrative_area_level_1') province = addressComp[i].short_name;
      if (type === 'country') country = addressComp[i].short_name;
      if (type === 'postal_code') postalCode = addressComp[i].short_name;
    }

    console.log('[onPlace] parsed:', { street, city, province, postalCode, country });

    const mapUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(placeData.geoCode[0].formatted_address)}`;

    dispatch({ type: TY.LINE1, payload: street.trim() });
    dispatch({ type: TY.CITY, payload: city });
    dispatch({ type: TY.PROVINCE, payload: province });
    dispatch({ type: TY.POSTAL_CODE, payload: postalCode });
    dispatch({ type: TY.COUNTRY, payload: country });
    dispatch({ type: TY.ONLINE_MAP_URL, payload: mapUrl });

    setValid((prev) => {
      const cleared = { isErr: false, errMsg: '', canNotSave: false };
      const next = { ...prev };
      if (street.trim()) next.Line1 = cleared;
      if (city) next.City = cleared;
      if (province) next.Province = cleared;
      if (postalCode) next.PostalCode = cleared;
      if (country) next.Country = cleared;
      return next;
    });
  };

  // Compute canNotSave
  const canNotSave = Object.values(valid).some((v) => v.canNotSave);

  // Save handler
  const onSaveForm = useCallback(async () => {
    const currentState = formState as AddressFormData;
    let hasErrors = false;
    const newValid = { ...valid };

    if (!currentState.Title?.trim()) {
      newValid.Title = { isErr: true, errMsg: 'Title is required', canNotSave: true };
      hasErrors = true;
    }
    if (!currentState.TypeId) {
      newValid.TypeId = { isErr: true, errMsg: 'Address type is required', canNotSave: true };
      hasErrors = true;
    }
    if (!currentState.Line1?.trim()) {
      newValid.Line1 = { isErr: true, errMsg: 'Address Line 1 is required', canNotSave: true };
      hasErrors = true;
    }
    if (!currentState.PostalCode?.trim()) {
      newValid.PostalCode = { isErr: true, errMsg: 'Postal code is required', canNotSave: true };
      hasErrors = true;
    }
    if (!currentState.PatrolType) {
      newValid.PatrolType = { isErr: true, errMsg: 'Patrol type is required', canNotSave: true };
      hasErrors = true;
    }

    if (hasErrors) {
      setValid(newValid);
      return;
    }

    const payload = makeAddressObj(currentState);
    if (onSave) {
      await onSave(payload, mode === 'edit');
    }
  }, [formState, valid, mode, onSave]);

  // Reset handler
  const onReset = useCallback(() => {
    if (mode === 'add') {
      const resetData = { ...initAddress, CustomerId: customerId || null };
      dispatch({ type: TY.RESET, payload: resetData });
      setValid({ ...initAddressValid });
    }
    if (mode === 'edit' && addressId) {
      const reload = async () => {
        try {
          const result = await getAddress(addressId);
          const addressData = result?.Data || result;
          const converted = convertAddress(addressData);
          dispatch({ type: TY.ADDRESS_CONVERT, payload: converted });
          setValid({ ...loadedAddressValid });
        } catch (error) {
          console.error('[useAddressFormUnified] Reset failed:', error);
        }
      };
      reload();
    }
  }, [mode, addressId, customerId]);

  const state = {
    address: formState,
    valid,
    isLoading,
    canNotSave,
  };

  const handler = {
    onForm,
    onTypeChange,
    onPlace,
    onSave: onSaveForm,
    onReset,
  };

  return { state, handler };
}

export default useAddressFormUnified;
